/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionList, Button, ButtonType, Drawer, Icon, Size } from '@vaisala-rockhopper/components';
import { VaiIcon } from '@vaisala-rockhopper/design-tokens';
import LanguageController from '../../../modules/LanguageController';
import EnhancedRadioGroup from '../inputs/EnhancedRadioGroup';
import IconButton from './IconButton';
import './MobileMenu.scss';
import { isMobileOnly } from 'react-device-detect';

interface MobileMenuProps {
  onClose: VoidFunction;
  isOpen: boolean;
  themeIndex: number;
  onChooseTheme: (ind: number) => void;
  onHelpOpen: VoidFunction;
  showPWAButton: boolean;
  onShowPWAPrompt: VoidFunction;
}

export const MobileMenu = ({
  onClose,
  isOpen,
  themeIndex,
  onChooseTheme,
  onHelpOpen,
  showPWAButton,
  onShowPWAPrompt,
}: MobileMenuProps) => {
  const { t } = useTranslation();
  const [openedAccordion, setOpenedAccordion] = React.useState('');

  const handleAccordionChange = (activeKeys: any, currentAccordionKey: React.SetStateAction<string>, isExpanded: any) => {
    setOpenedAccordion(currentAccordionKey as string);
  };

  const themeOptions = ['ui.theme.light', 'ui.theme.dark'];

  const renderButton = (icon: string, label: string, onClick: VoidFunction) => (
    <>
      <Button className="menu-button" buttonType={ButtonType.Link} buttonSize={Size.M} startIcon={<Icon name={icon} />} onClick={() => onClick()}>
        {t(label)}
      </Button>
      <br />
    </>
  );

  const renderOptions = (ind: number, title: string, current: number, options: string[], onChange: (i: number) => void) => {
    const key = `acc${ind}`;
    return (
      <Accordion
        accordionKey={key}
        title={t(title)}
        isExpanded={key === openedAccordion}
        summary={key === openedAccordion ? '' : t(options[current])}
        summaryWidth="50%"
      >
        <EnhancedRadioGroup options={options} value={current} onChange={onChange} indexAsValue={true} />
      </Accordion>
    );
  };

  if (isMobileOnly) {
    return (
      <Drawer className="mobile-menu" anchor={'Right'} isOpen={isOpen}>
        <div className="close-button-wrap">
          <IconButton variant="dark" buttonSize={Size.S} onClick={onClose}>
            <Icon name={VaiIcon.Cross} />
          </IconButton>
        </div>

        <div className="content">
          <AccordionList onChange={handleAccordionChange} defaultKeys={openedAccordion} exclusive={true}>
            {renderOptions(
              0,
              'ui.language',
              LanguageController.currentLanguageIndex,
              LanguageController.languageNames,
              LanguageController.changeLanguageByIndex
            )}
            {renderOptions(2, 'ui.theme', themeIndex, themeOptions, onChooseTheme)}
          </AccordionList>
          <br />
          <br />
          {renderButton(VaiIcon.Help, 'ui.help', onHelpOpen)}
          {showPWAButton && renderButton(VaiIcon.Download, 'ui.install', onShowPWAPrompt)}
        </div>
      </Drawer>
    );
  } else return null;
};
